// src/features/documents/api.ts
import { api } from '@/lib/api';
import type { AllLinksResponse, DocumentDetail, DocumentsResponse, SemanticSearchResponse, TagInfo } from './types';

const DMS_BASE = '/v4/dms';

const PAGE_SIZE = 50;

// --- Stats ---

export interface DmsStats {
  totalDocuments: number;
  totalSize: number;
  ready: number;
  pending: number;
  failed: number;
}

export async function getDocumentStats(): Promise<DmsStats> {
  return api.get<DmsStats>(`${DMS_BASE}/documents/stats`);
}

// --- Documents ---

export async function getDocuments(cursor: string | null): Promise<DocumentsResponse> {
  const params: Record<string, string> = { limit: String(PAGE_SIZE) };
  if (cursor) params.cursor = cursor;
  return api.get<DocumentsResponse>(`${DMS_BASE}/documents`, params);
}

export async function searchDocuments(query: string): Promise<DocumentsResponse> {
  return api.get<DocumentsResponse>(`${DMS_BASE}/documents/search`, {
    q: query,
    mode: 'all',
    limit: String(PAGE_SIZE),
  });
}

export async function searchDocumentsSemantic(query: string): Promise<SemanticSearchResponse> {
  return api.get<SemanticSearchResponse>(`${DMS_BASE}/documents/search`, {
    q: query,
    mode: 'semantic',
    limit: String(PAGE_SIZE),
  });
}

/**
 * Sanitize HTML from searchHeadline — only allow <mark> tags.
 * Strips everything else to prevent XSS from document content.
 */
export function sanitizeHtml(html: string): string {
  return html.replace(/<\/?(?!mark\b)[a-z][^>]*>/gi, '');
}

/**
 * Google Drive copy of a document, when there is one.
 * The n8n filer records drive_web_view_link / drive_file_id in the DMS
 * metadata; older Drive uploads only carry `gdrive:<fileId>` as sourcePath.
 */
export function driveWebViewLink(doc: {
  metadata?: Record<string, unknown> | null;
  currentVersion?: { sourcePath?: string | null } | null;
}): string | null {
  const link = doc.metadata?.drive_web_view_link;
  if (typeof link === 'string' && link.startsWith('http')) return link;
  const id = doc.metadata?.drive_file_id;
  const fileId =
    (typeof id === 'string' && id ? id : null) ?? /^gdrive:(.+)$/.exec(doc.currentVersion?.sourcePath ?? '')?.[1] ?? null;
  return fileId ? `https://drive.google.com/file/d/${fileId}/view` : null;
}

export async function getDocumentDetail(id: string): Promise<{ data: DocumentDetail }> {
  return api.get<{ data: DocumentDetail }>(`${DMS_BASE}/documents/${id}`);
}

export async function getDocumentPreviewUrl(id: string): Promise<{ previewUrl: string; mimeType: string }> {
  return api.get<{ previewUrl: string; mimeType: string }>(`${DMS_BASE}/documents/${id}/preview-url`);
}

export async function getDocumentDownloadUrl(id: string): Promise<{ downloadUrl: string }> {
  return api.get<{ downloadUrl: string }>(`${DMS_BASE}/documents/${id}/download-url`);
}

export async function recordDocumentView(id: string): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>(`${DMS_BASE}/documents/${id}/view`, {});
}

export async function toggleDocumentReaction(
  id: string,
  reaction: 'up' | 'down',
): Promise<{ reaction: 'up' | 'down' | null }> {
  return api.post<{ reaction: 'up' | 'down' | null }>(`${DMS_BASE}/documents/${id}/react`, { reaction });
}

// --- Upload ---

export interface DuplicateMatch {
  documentId: string;
  documentName: string;
  originalName: string;
  createdAt: string;
}

export async function computeFileChecksum(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function checkDuplicate(checksum: string): Promise<{ duplicates: DuplicateMatch[] }> {
  return api.post<{ duplicates: DuplicateMatch[] }>(`${DMS_BASE}/documents/check-duplicate`, { checksum });
}

export async function getUploadUrl(
  filename: string,
  mimeType: string,
  opts?: { uploadSource?: string },
): Promise<{ documentId: string; versionId: string; uploadUrl: string; expiresAt: string }> {
  return api.post<{ documentId: string; versionId: string; uploadUrl: string; expiresAt: string }>(
    `${DMS_BASE}/documents/upload-url`,
    { filename, mimeType, ...opts },
  );
}

export async function uploadToSignedUrl(url: string, file: File): Promise<void> {
  const contentType = file.type || 'application/octet-stream';
  const response = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const text = await response.text();
      if (text) detail += `: ${text.slice(0, 200)}`;
    } catch {
      // ignore
    }
    throw new Error(`Failed to upload file to storage (${detail})`);
  }
}

export interface UploadConfirmation {
  documentId: string;
  versionId?: string;
  status?: 'queued' | 'ready' | 'failed';
  resultUrl?: string;
  deduplicated?: boolean;
  conversionStatus?: string;
  conversionError?: string | null;
  data?: unknown;
}

export async function confirmUpload(
  id: string,
  size: number,
  checksum?: string,
): Promise<UploadConfirmation> {
  const result = await api.post<Omit<UploadConfirmation, 'documentId'> & { documentId?: string }>(`${DMS_BASE}/documents/${id}/confirm`, {
    size,
    checksum,
  });
  // Older DMS deployments omit the top-level ID when no canonical dedup occurs.
  return { ...result, documentId: result.documentId || id };
}

export async function uploadNewVersion(
  documentId: string,
  filename: string,
  mimeType: string,
): Promise<{ versionId: string; version: number; uploadUrl: string; expiresAt: string }> {
  return api.post<{ versionId: string; version: number; uploadUrl: string; expiresAt: string }>(
    `${DMS_BASE}/documents/${documentId}/versions`,
    { filename, mimeType },
  );
}

// --- Duplicates ---

export interface DuplicateDocument {
  documentId: string;
  name: string;
  documentDate: string | null;
  createdAt: string;
  originalName: string;
  sourceSize: number | null;
  sourceMimeType: string | null;
  linkCount: number;
  versionCount: number;
}

export interface DuplicateGroup {
  checksum: string;
  documents: DuplicateDocument[];
}

export async function getDuplicates(): Promise<{ data: DuplicateGroup[] }> {
  return api.get<{ data: DuplicateGroup[] }>(`${DMS_BASE}/duplicates`);
}

export async function resolveDuplicate(params: {
  action: 'keep';
  keepDocumentId: string;
  deleteDocumentIds: string[];
}): Promise<{ success: boolean; deletedCount: number }> {
  return api.post<{ success: boolean; deletedCount: number }>(`${DMS_BASE}/duplicates/resolve`, params);
}

// --- Actions ---

export async function reconvertDocument(id: string): Promise<{ success: boolean; version?: number }> {
  return api.post<{ success: boolean; version?: number }>(`${DMS_BASE}/documents/${id}/reconvert`, {});
}

export async function retryFailedDocuments(): Promise<{ success: boolean; reset: number }> {
  return api.post<{ success: boolean; reset: number }>(`${DMS_BASE}/documents/retry-failed`, {});
}

export async function reembedDocument(id: string): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>(`${DMS_BASE}/documents/${id}/reembed`, {});
}

export async function reclassifyDocument(id: string): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>(`${DMS_BASE}/documents/${id}/reclassify`, {});
}

export async function reprocessDocument(id: string): Promise<{ success: boolean; results?: Record<string, unknown> }> {
  return api.post<{ success: boolean; results?: Record<string, unknown> }>(`${DMS_BASE}/documents/${id}/reprocess`, {});
}

// --- Links ---

export async function getDocumentLinks(id: string): Promise<AllLinksResponse> {
  return api.get<AllLinksResponse>(`${DMS_BASE}/documents/${id}/links`);
}

// --- References ---

export interface DocumentReferences {
  total: number;
  documentLinks: number;
  invoiceDocuments: number;
  attachments: number;
  accStatements: number;
}

export async function getDocumentReferences(id: string): Promise<DocumentReferences> {
  return api.get<DocumentReferences>(`${DMS_BASE}/documents/${id}/references`);
}

// --- Tags ---

export async function getTags(): Promise<{ data: TagInfo[] }> {
  return api.get<{ data: TagInfo[] }>(`${DMS_BASE}/tags`);
}
